/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnChanges, type OnInit, type SimpleChanges, ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core';
import { I18nService, TimeService } from '@dm/core';

import { AnalysisStrService } from '../../../../../monitor/analysis/analysis-str.service';
import { DEFAULT_STRING_VALUE } from '../../const';
import { InfigoService } from '../../infigo.service';

@Component({
    selector: 'app-performance-chart',
    templateUrl: './performance-chart.component.html',
    styleUrls: ['./performance-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerformanceChartComponent implements OnInit, OnChanges {
    @Input() data;
    @Input() loading = false;
    @ViewChild('chartComponent') chartComponent;
    value: any = DEFAULT_STRING_VALUE;
    unit: any = '';
    title;
    color = 'black';
    echartsOption;
    defaultPerformanceMap = {
        22: { color: '#6C92FA', title: this.i18n.get('home.performance_term_totalIOPS_label') },
        21: { color: '#A97AF8', title: this.i18n.get('common.bandwidth_label') },
        370: { color: '#6CBFFF', title: this.i18n.get('home.performance_term_delay_lab') },
        523: { color: '#6C92FA', title: this.i18n.get('monitor.charts_term_serviceTime_lab') },
        511: { color: '#A97AF8', title: this.i18n.get('home.performance_term_fileBandwidth_lab') },
        182: { color: '#6CBFFF', title: this.i18n.get('monitor.performance_term_indicator_182') },
    };

    constructor(public str: AnalysisStrService,
        private infigoService: InfigoService,
        private timeService: TimeService,
        public i18n: I18nService) {
    }

    getTimeString(timeValue): string {
        return this.timeService.formatUTC(Number(timeValue), 'YYYY-MM-DD HH:mm:ss');
    }

    ngOnInit(): void {
        this.handleData();
    }

    handleData(): void {
        const performanceMap = this.defaultPerformanceMap;
        const computeData = this.data.indicator_values ? this.data.indicator_values.filter((i) => !Number.isNaN(Number(i))) : [];
        if (!this.data.indicator || !computeData.length) {
            this.color = (performanceMap[this.data] || performanceMap[this.data.indicator]).color;
            this.title = (performanceMap[this.data] || performanceMap[this.data.indicator]).title;
            this.value = DEFAULT_STRING_VALUE;
            this.unit = '';
            return;
        }
        const { unit } = this.str.getIndicatorNameAndUnit(this.data.indicator);
        const { color, title } = performanceMap[this.data.indicator];
        this.color = color;
        this.title = title;
        this.value = this.getAverageData(computeData);
        [this.value, this.unit] = this.infigoService.adaptValue(this.value, unit, this.data.indicator === 182 ? 0 : 3);
        this.echartsOption = {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'line',
                    lineStyle: {
                        color: '#999',
                        type: 'dashed',
                    },
                    axis: 'x',
                    snap: true,
                },
                appendToBody: true,
                formatter: (params) => `
                        <div>
                            ${this.getTimeString(params[0].axisValue)}
                        </div>
                        <div style="display: flex;align-items: center;">
                                <div style="width:1em;height: 1em;border-radius: 50%;background: ${color};margin-right: 8px"></div>
                                <div>
                                  ${this.infigoService.adaptValue(Number(params[0].data), unit, 3).join(' ')}
                                </div>
                        </div>
                    `,
                padding: 10,
            },
            grid: {
                top: 10,
                bottom: 10,
            },
            xAxis: {
                type: 'category',
                show: false,
                data: this.data.timestamp,
                tooltip: {
                    trigger: 'none',
                    axisPointer: {
                        type: 'none',
                    },
                },
            },
            yAxis: {
                type: 'value',
                show: false,
            },
            color,
            series: [{
                name: title,
                data: this.data.indicator_values,
                type: 'line',
                symbol: 'none',
                smooth: true,
            }],
            animationDurationUpdate: 1000,
        };
    }

    getAverageData(arrData: Array<number>): number {
        return arrData.reduce((pre, cur) => Number(cur) + pre, 0) / arrData.length;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.data && !changes.data.firstChange) {
            // 确保loading已经消失再重播动画
            setTimeout(() => this.animateReplay());
        }
    }

    animateReplay(): void {
        if (this.chartComponent) {
            this.chartComponent.chartObj.clear();
            this.handleData();
            this.chartComponent.chartObj.setOption(this.echartsOption);
        }
    }
}


